import { useState, useCallback } from "react";
import { Search, Sparkles, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { articles } from "@/data/articles";
import ArticleCard from "@/components/ArticleCard";
import { useToast } from "@/hooks/use-toast";

interface SearchResult {
  index: number;
  reason: string;
}

interface AISearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SEARCH_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-search`;

const AISearchDialog = ({ open, onOpenChange }: AISearchDialogProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const { toast } = useToast();

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    setIsSearching(true);
    setHasSearched(true);
    setResults([]);

    try {
      // Send a compact version of articles to the AI
      const compactArticles = articles.map((a) => ({
        title: a.title,
        summary: a.summary,
        category: a.category,
        tags: a.tags,
      }));

      const resp = await fetch(SEARCH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ query: query.trim(), articles: compactArticles }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Search failed" }));
        if (resp.status === 429) {
          toast({ variant: "destructive", title: "Rate Limited", description: err.error });
        } else if (resp.status === 402) {
          toast({ variant: "destructive", title: "Credits Exhausted", description: err.error });
        } else {
          toast({ variant: "destructive", title: "Search Error", description: err.error });
        }
        return;
      }

      const data = await resp.json();
      setResults(data.results || []);
    } catch {
      toast({ variant: "destructive", title: "Search Error", description: "Could not complete search. Please try again." });
    } finally {
      setIsSearching(false);
    }
  }, [query, toast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setHasSearched(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-card border-border p-0">
        <DialogTitle className="sr-only">AI-Powered Search</DialogTitle>
        {/* Search input */}
        <div className="sticky top-0 bg-card z-10 p-4 border-b border-divider">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
              <Input
                placeholder="Search or Ask AI about energy markets..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10 pr-8 bg-surface border-border text-foreground placeholder:text-muted-foreground"
                autoFocus
              />
              {query && (
                <button onClick={handleClear} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} disabled={isSearching || !query.trim()} size="sm" className="gap-1.5">
              {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Powered by AI — ask natural language questions like "What's happening with OPEC production?" or "Latest on green shipping fuels"
          </p>
        </div>

        {/* Results */}
        <div className="p-4">
          {isSearching && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Searching articles with AI...</p>
            </div>
          )}

          {!isSearching && hasSearched && results.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No matching articles found. Try a different question.</p>
            </div>
          )}

          {!isSearching && results.length > 0 && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                {results.length} result{results.length !== 1 ? "s" : ""} found
              </p>
              {results.map((result) => {
                const article = articles[result.index];
                if (!article) return null;
                return (
                  <div key={result.index} onClick={() => onOpenChange(false)}>
                    <ArticleCard article={article} variant="compact" />
                    <p className="text-xs text-primary/70 italic ml-2 -mt-2 mb-2 pl-24">
                      {result.reason}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {!isSearching && !hasSearched && (
            <div className="text-center py-12 space-y-3">
              <Sparkles className="w-10 h-10 text-primary/40 mx-auto" />
              <p className="text-muted-foreground text-sm">Ask any question about energy markets and commodities</p>
              <div className="flex flex-wrap justify-center gap-2">
                {["OPEC+ production cuts", "LNG shipping trends", "Green fuel alternatives", "Iron ore demand from China"].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => { setQuery(suggestion); }}
                    className="text-xs px-3 py-1.5 rounded-full bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AISearchDialog;
